// Privacy Policy — required for Google Ads / GA4 remarketing & conversion
// tracking, GDPR compliance, and standard EU consumer transparency. Kept
// intentionally minimal + factual. Update the effective date only when the
// text below actually changes.

import { CONTACT } from "@/data/site";

const EFFECTIVE_DATE = "10 July 2026";

const Privacy = () => {
  return (
    <main
      data-testid="privacy-page"
      className="max-w-3xl mx-auto px-5 lg:px-10 pt-28 pb-24 text-foreground/85"
    >
      <h1 className="font-display text-4xl md:text-5xl text-gold mb-2">Privacy Policy</h1>
      <p className="text-xs uppercase tracking-[0.18em] text-foreground/50 mb-10">
        Effective: {EFFECTIVE_DATE}
      </p>

      <section className="space-y-4 text-sm leading-relaxed">
        <p>
          Athens Airport Taxi Transfers (&quot;we&quot;, &quot;us&quot;) is a licensed private
          transfer service operating in Attica, Greece. This policy explains
          what personal data we collect when you use{" "}
          <a
            className="text-gold underline hover:no-underline"
            href="https://athensairporttaxitransfers.com"
          >
            athensairporttaxitransfers.com
          </a>
          , why we collect it, and the rights you have over it under the EU
          General Data Protection Regulation (GDPR) and Greek Law 4624/2019.
        </p>
      </section>

      <h2 className="font-display text-2xl text-gold mt-10 mb-3">1. Data we collect</h2>
      <ul className="list-disc list-outside pl-5 space-y-2 text-sm">
        <li>
          <strong>Booking data</strong> — name, email, phone number, pickup and
          drop-off addresses, travel date &amp; time, passenger and luggage
          count, optional flight number and special requests.
        </li>
        <li>
          <strong>Payment data</strong> — card and payment information are
          entered directly on Stripe&apos;s PCI-DSS certified checkout page. We
          never see or store your card number. We only store the Stripe
          session ID, the amount, and the payment status.
        </li>
        <li>
          <strong>Technical data</strong> — anonymous analytics (Google
          Analytics 4) about the pages you visited, the device you used, and
          approximate location (country / city level).
        </li>
      </ul>

      <h2 className="font-display text-2xl text-gold mt-10 mb-3">2. Why we use it</h2>
      <ul className="list-disc list-outside pl-5 space-y-2 text-sm">
        <li>To dispatch your driver and confirm the booking by email.</li>
        <li>
          To process your payment via Stripe (legal basis: performance of
          contract).
        </li>
        <li>
          To improve the site and measure the effectiveness of our Google Ads
          campaigns (legal basis: legitimate interest and, where applicable,
          consent).
        </li>
        <li>To meet Greek tax record-keeping obligations (legal basis: legal obligation).</li>
      </ul>

      <h2 className="font-display text-2xl text-gold mt-10 mb-3">3. Who we share it with</h2>
      <p className="text-sm">
        We share the minimum information required with a small number of
        service providers that help us run the site:
      </p>
      <ul className="list-disc list-outside pl-5 space-y-2 text-sm mt-3">
        <li><strong>Stripe</strong> (payment processing, Ireland / United States)</li>
        <li><strong>Resend</strong> (email delivery for booking confirmations)</li>
        <li><strong>Google</strong> (Analytics 4, Google Ads, Maps &amp; Places API)</li>
        <li>The <strong>driver</strong> assigned to your ride</li>
      </ul>
      <p className="text-sm mt-3">
        We do not sell your personal data to anyone.
      </p>

      <h2 className="font-display text-2xl text-gold mt-10 mb-3">4. How long we keep it</h2>
      <p className="text-sm">
        Booking records are kept for the period required by Greek tax law
        (currently five years from the end of the fiscal year). Analytics data
        is retained for up to 14 months. You may request earlier deletion at
        any time (see below).
      </p>

      <h2 className="font-display text-2xl text-gold mt-10 mb-3">5. Your rights</h2>
      <p className="text-sm">
        Under the GDPR you have the right to access, correct, delete, restrict
        or port your personal data, and to object to processing based on
        legitimate interest. You may exercise any of these rights by emailing{" "}
        <a className="text-gold underline hover:no-underline" href={`mailto:${CONTACT.email}`}>
          {CONTACT.email}
        </a>
        . You also have the right to lodge a complaint with the Hellenic Data
        Protection Authority (dpa.gr).
      </p>

      <h2 className="font-display text-2xl text-gold mt-10 mb-3">6. Cookies &amp; tracking</h2>
      <p className="text-sm">
        We use essential cookies to remember your booking session and Google
        Analytics 4 cookies to understand how visitors use the site. You can
        block or clear these at any time in your browser settings.
      </p>

      <h2 className="font-display text-2xl text-gold mt-10 mb-3">7. Contact</h2>
      <p className="text-sm">
        Athens Airport Taxi Transfers · {CONTACT.address}
        <br />
        Email:{" "}
        <a className="text-gold underline hover:no-underline" href={`mailto:${CONTACT.email}`}>
          {CONTACT.email}
        </a>
        <br />
        Phone:{" "}
        <a className="text-gold underline hover:no-underline" href={`tel:+${CONTACT.phoneRaw}`}>
          {CONTACT.phone}
        </a>
      </p>
    </main>
  );
};

export default Privacy;
